import React from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useWebsite } from '../../Data/Website';
import Form, { RegexValidator, RequiredValidator, TextField, Actions } from '../../Components/Form';
import Alert from '../../Components/Alert';
import { PageLoading } from '../../Components/Loading';
import Button from '../../Components/Button/Button';
import Grid from '../../Components/Formatting/Grid';
import CopyableValue from '../../Components/Formatting/CopyableValue';
import { useDialogues } from '../../Components/Dialogues';

const CustomDomain = () => {
    const {website, addCustomDomain, removeCustomDomain} = useWebsite(true);

    return <>
        <TopBar left="/more" title="Custom domain" />
        <Page>
            {website && <Inner {...{website, addCustomDomain, removeCustomDomain}} />}
        </Page>
    </>
}

const Inner = ({website, addCustomDomain, removeCustomDomain}) => {
    if (website.type !== 'getsoapy') {
        return <Alert theme="error" title="Not available" message="This feature is only available for getSoapy websites." />
    }

    if (website.customDomainConfig === null) {
        return <InitialSetup {...{website, addCustomDomain}} />
    }

    return <AlreadySetup {...{website, removeCustomDomain}} />
}

const InitialSetup = ({website, addCustomDomain}) => {
    return <>
        <div className="prose prose-sm mb-4">
            <p>
                Having a custom domain like '{website.soapySubdomain}.com' makes your business look more professional and trustworthy,
                while a subdomain like '{website.soapySubdomain}.getsoapy.com' can seem less official and harder for customers to remember.
            </p>

            <p>
                If you don't already have a domain you can use any domain registrar,
                such as <a href="https://namecheap.com" target="_blank" rel="noreferrer">namecheap.com</a>.
            </p>
        </div>

        <Form onSubmit={addCustomDomain}>
            <TextField name="domain"
                label="Domain"
                validators={[
                    RequiredValidator,
                    RegexValidator(/^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/, 'Please enter a valid domain name'),
                ]}
            />

            <Actions submit={{label: 'Add domain'}} cancel />
        </Form>
    </>
}

const AlreadySetup = ({website, removeCustomDomain}) => {
    const isDeleting = website?.customDomainConfig?.isDeleting === true;
    const {confirm} = useDialogues();

    return <>
        {isDeleting && <Alert theme="danger" title="Removing" message="Your custom domain is being removed" loading />}

        <div className="prose prose-sm mb-4">
            <p>
                Please update your DNS records as shown below.<br />
                Check with your domain registrar's help section if you're unsure how to do this.
            </p>

            <p>
                After you make changes it can take some time for the updates to propagate across the internet.<br />
                Your website will be live on your custom domain once the changes have fully taken effect.
            </p>
        </div>

        <DNSRecords customDomainConfig={website.customDomainConfig} />

        <Button theme="danger"
            className="mt-4"
            icon="Trash"
            label="Remove custom domain"
            // Make sure it's setup first before trying to delete it, otherwise it'll error
            disabled={isDeleting || !website?.customDomainConfig || !website?.customDomainConfig?.aRecords || !website?.customDomainConfig?.dnsValidationRecords}
            onClick={() => confirm(
                {
                    theme: 'danger',
                    title: 'Remove custom domain',
                    message: 'Are you sure you want to remove your custom domain?',
                },
                () => removeCustomDomain()
            )}
        />
    </>
}

const DNSRecords = ({customDomainConfig}) => {
    if (!customDomainConfig || !customDomainConfig.aRecords || !customDomainConfig.dnsValidationRecords) {
        return <PageLoading message="Preparing your custom domain" short />
    }

    return <div>
        <Grid className="mb-4" items={[
            [
                <strong>Type</strong>,
                'A Record'
            ],
            [
                <strong>Name</strong>,
                <CopyableValue value={customDomainConfig.domain} />,
            ],
            [
                <strong>Values</strong>,
                customDomainConfig.aRecords.map(ip => <>
                    <CopyableValue key={ip} value={ip} className="mb-1" />
                </>),
            ],
            [
                <>&nbsp;</>,
                <>&nbsp;</>,
            ],
            [
                <strong>Type</strong>,
                customDomainConfig.dnsValidationRecords.type
            ],
            [
                <strong>Name</strong>,
                <CopyableValue value={customDomainConfig.dnsValidationRecords.name} />,
            ],
            [
                <strong>Value</strong>,
                <CopyableValue value={customDomainConfig.dnsValidationRecords.value} />,
            ],
        ]} />
    </div>
/*
    return <Scroll>
        <dl className="row mb-2">
            <Row left="3" title="Type" content="A" />
            <Row left="3" title="Name" content={<ClickToCopy text={customDomainConfig.domain} />} />
            <Row left="3" title="Value" content={
                customDomainConfig.aRecords.map(a => <React.Fragment key={a}>
                    <ClickToCopy text={a} />
                    <br />
                </React.Fragment>)
            } />
        </dl>

        <dl className="row border-top pt-3">
            <Row left="3" title="Type">
                <ClickToCopy text={customDomainConfig.dnsValidationRecords.type} />
            </Row>
            <Row left="3" title="Name">
                <ClickToCopy text={customDomainConfig.dnsValidationRecords.name} />
            </Row>
            <Row left="3" title="Value">
                <ClickToCopy text={customDomainConfig.dnsValidationRecords.value} />
            </Row>
        </dl>
    </Scroll> */
}

export default CustomDomain;
